import React, { FunctionComponent } from "react";
import { ScrollView, StyleSheet, View, Image } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

import Text from "../../components/Text";
import { summarizeAddress } from "../../utils/address";
import IconButton from "../../components/IconButton";
import { formatDateOfBirth } from "../../utils/customer";
import { colors } from "../../theme";
import InformationCell from "../../components/InformationCell";
import Container from "../../components/Container";
import { useApolloClient } from "@apollo/client";
import { Customer as customerFragment } from "../../fragments";
import { useFocusEffect } from "@react-navigation/native";
import { Customer } from "../../types";
import { usePresignedImageUrl } from "../../hooks/usePresignedImageUrl";

interface ViewProfileScreenProps {
  route: any;
  navigation: any;
}

const ViewProfileScreen: FunctionComponent<ViewProfileScreenProps> = ({
  navigation,
  route,
}) => {
  const { customerId } = route.params;
  const [customer, setCustomer] = React.useState<Customer | null>(null);
  const client = useApolloClient();
  const { url: idImageUrl } = usePresignedImageUrl(customer?.idImageUrls?.[0]);

  // This is a hack because the readFragment call wasn't updating
  // once the customer has changed when the profile has been edited :-|
  const getAndSetCustomer = React.useCallback(() => {
    const customer = client.readFragment({
      fragment: customerFragment,
      id: `Customer:${customerId}`,
      fragmentName: "Customer",
    });

    setCustomer(customer);
  }, [customerId]);

  React.useEffect(getAndSetCustomer);
  useFocusEffect(getAndSetCustomer);

  React.useLayoutEffect(() => {
    navigation.setOptions({
      title: customer?.name,
      headerRight: () => (
        <IconButton
          icon="edit"
          onPress={() =>
            navigation.navigate("EditProfile", { ...route.params })
          }
          style={{ marginRight: 15 }}
        />
      ),
    });
  }, [navigation, customer]);

  return (
    <SafeAreaView style={styles.container} edges={["right", "bottom", "left"]}>
      <Container>
        <ScrollView style={styles.scrollView}>
          <View style={styles.innerContainer}>
            <InformationCell label="Name">
              <Text size="l" variant="bold" style={styles.text}>
                {customer?.name || "N/A"}
              </Text>
            </InformationCell>

            <InformationCell label="Address">
              <Text size="l" variant="bold" style={styles.text}>
                {customer?.address
                  ? summarizeAddress(customer?.address)
                  : "N/A"}
              </Text>
            </InformationCell>

            <InformationCell label="Date of Birth">
              <Text size="l" variant="bold" style={styles.text}>
                {customer?.dateOfBirth
                  ? formatDateOfBirth(customer?.dateOfBirth)
                  : "N/A"}
              </Text>
            </InformationCell>

            <InformationCell label="Phone Number">
              <Text size="l" variant="bold" style={styles.text}>
                {customer?.phoneNumber || "N/A"}
              </Text>
            </InformationCell>

            <InformationCell label="Email Address">
              <Text size="l" variant="bold" style={styles.text}>
                {customer?.emailAddress || "N/A"}
              </Text>
            </InformationCell>

            <InformationCell label="ID on file">
              {customer?.idImageUrls && customer.idImageUrls.length > 0 ? (
                idImageUrl && (
                  <Image
                    source={{ uri: idImageUrl }}
                    style={styles.idImagePreview}
                  />
                )
              ) : (
                <Text size="l" variant="bold" style={styles.text}>
                  N/A
                </Text>
              )}
            </InformationCell>
          </View>
        </ScrollView>
      </Container>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: colors.black,
    flex: 1,
    padding: 20,
    paddingTop: 0,
  },
  scrollView: {
    flex: 1,
  },
  innerContainer: {
    paddingVertical: 20,
    flex: 1,
  },
  informationCell: {
    marginBottom: 10,
  },
  cellLabel: {
    color: colors.darkGrey,
  },
  text: {
    color: colors.white,
  },
  idImagePreview: {
    width: 140,
    height: 160,
    borderRadius: 10,
    marginTop: 5,
  },
  footer: {
    marginTop: "auto",
  },
  appVersion: {
    color: colors.darkGrey,
    marginBottom: 10,
    textAlign: "center",
  },
  cellContainer: {
    backgroundColor: colors.white,
    borderRadius: 20,
    padding: 20,
    marginBottom: 10,
  },
});

export default ViewProfileScreen;
